
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CountryWithRegion {
  name: string;
  code: string;
  active: boolean;
  background_image_url?: string | null;
  background_image_alt?: string | null;
  region?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

export const useCountriesWithRegions = () => {
  const [countries, setCountries] = useState<CountryWithRegion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCountries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use manual join approach to fetch countries and regions separately
      const [countriesResult, regionsResult] = await Promise.all([
        supabase
          .from('countries')
          .select('name, code, active, background_image_url, background_image_alt, region_id')
          .order('name'),
        supabase
          .from('regions')
          .select('id, name, code')
      ]);

      if (countriesResult.error) throw countriesResult.error;
      if (regionsResult.error) throw regionsResult.error;

      const countries = countriesResult.data || [];
      const regions = regionsResult.data || [];
      
      console.log('✅ Countries fetched:', countries.length);
      console.log('✅ Regions fetched:', regions.length);
      console.log('🔍 Sample country with region_id:', countries.find(c => c.region_id));
      console.log('🔍 Sample region:', regions[0]);
      console.log('🔍 Region IDs:', regions.map(r => r.id));
      console.log('🔍 Countries with region_id:', countries.filter(c => c.region_id).length);
      
      // Manual join countries with their regions
      const joinedData = countries.map(country => {
        if (country.region_id) {
          const matchedRegion = regions.find(region => region.id === country.region_id);
          console.log(`🔍 Country ${country.name} region_id: ${country.region_id}, matched:`, matchedRegion ? matchedRegion.name : 'NOT FOUND');
        }
        
        return {
          name: country.name,
          code: country.code,
          active: country.active,
          background_image_url: country.background_image_url,
          background_image_alt: country.background_image_alt,
          region: country.region_id 
            ? regions.find(region => region.id === country.region_id) || null
            : null
        };
      });
      
      console.log('✅ Countries with regions:', joinedData.filter(c => c.region).length);
      setCountries(joinedData);
    } catch (err) {
      console.error('❌ Error fetching countries:', err);
      setError('Failed to fetch countries');
      toast.error('Failed to load countries');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  return { countries, isLoading, error, refetch: fetchCountries };
};
